import React from "react";
import { motion } from "framer-motion";
import { FiBriefcase, FiMapPin, FiBookOpen } from "react-icons/fi";
import SectionHead from "../ui/SectionHead";
import CountUp from "../ui/CountUp";
import {
  educationHistory,
  principles,
  profile,
  stats,
  workExperience
} from "../../data/resume";
import { fadeUp, staggerChildren, trackSpotlight } from "../../utils/motionPresets";
import portrait from "../../assets/portrait.webp";
import "./about.css";

const current = workExperience[0];
const education = educationHistory[0];

const About = () => (
  <section className="section" id="about">
    <div className="container">
      <SectionHead
        index="01"
        eyebrow="About"
        title={
          <>
            Engineer by craft,
            <br />
            <span className="serif">partner</span> by approach.
          </>
        }
        lede="I turn complex business requirements into resilient software — and stay close to the people I build for, from first sketch to production."
      />

      <motion.div
        className="bento"
        variants={staggerChildren}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.1 }}
      >
        <motion.figure className="card bento__portrait" variants={fadeUp} onPointerMove={trackSpotlight}>
          <div className="bento__portrait-bg" aria-hidden="true" />
          <img src={portrait} alt="Portrait of Yaseen Al-Midani" loading="lazy" width="1000" height="1115" />
          <figcaption className="bento__portrait-caption">
            <span>
              <strong>{profile.name}</strong>
              <small>{profile.role}</small>
            </span>
            <span className="bento__location">
              <FiMapPin /> {profile.city}
            </span>
          </figcaption>
        </motion.figure>

        <motion.div className="card bento__bio" variants={fadeUp} onPointerMove={trackSpotlight}>
          <p className="bento__bio-text">
            {profile.bio[0]}
            <span className="accent">{profile.bio[1]}</span>
            {profile.bio[2]}
          </p>
        </motion.div>

        <motion.div className="bento__stats" variants={staggerChildren}>
          {stats.map((stat) => (
            <motion.div
              key={stat.label}
              className="card bento__stat"
              variants={fadeUp}
              onPointerMove={trackSpotlight}
            >
              <span className="bento__stat-value">
                <CountUp value={stat.value} suffix={stat.suffix} />
              </span>
              <span className="bento__stat-label">{stat.label}</span>
            </motion.div>
          ))}
        </motion.div>

        <motion.div className="card bento__info" variants={fadeUp} onPointerMove={trackSpotlight}>
          <span className="bento__label">
            <FiBriefcase /> Currently
          </span>
          <h3>{current.role}</h3>
          <p>
            at <strong>{current.company}</strong> · since {current.period.split(" – ")[0]}
          </p>
          <p className="bento__muted">{current.location}</p>
        </motion.div>

        <motion.div className="card bento__info" variants={fadeUp} onPointerMove={trackSpotlight}>
          <span className="bento__label">
            <FiBookOpen /> Education
          </span>
          <h3>{education.degree}</h3>
          <p>
            <strong>{education.institution}</strong> · {education.period}
          </p>
          <p className="bento__muted">{education.highlights[0]}</p>
        </motion.div>

        <motion.div className="card bento__principles" variants={fadeUp} onPointerMove={trackSpotlight}>
          <span className="bento__label">How I work</span>
          <ol>
            {principles.map((item, i) => (
              <li key={item.title}>
                <span className="bento__principle-index mono">0{i + 1}</span>
                <span>
                  <strong>{item.title}</strong>
                  <small>{item.text}</small>
                </span>
              </li>
            ))}
          </ol>
        </motion.div>
      </motion.div>
    </div>
  </section>
);

export default About;
